import React from "react";
import cx from "clsx";
import { isMobileOnly } from "react-device-detect";
import { ScreenClassProvider, ScreenClassRender } from "react-grid-system";
import { navigate } from "gatsby";
import qs from "query-string";

import GalleryModal from "@/components/shared/galleryModal";
import FilmModal from "@/components/shared/filmModal";
import Dimmer from "@/components/shared/dimmer";
import LandscapeBlock from "@/components/images/shared/landscape-block";
import {
  debounce,
  lockBodyScrolling,
  restoreBodyScrolling,
  isDesktop as _isDesktop
} from "@/helpers";

import globalStyles from "@/styles/global.module.scss";
import styles from "@/components/shared/withModals.module.scss";

const withModals = WrappedComponent => {
  return class GalleryHelper extends React.PureComponent {
    state = {
      hasMounted: false,
      userIsTabbing: false,
      galleryModalMounted: false,
      galleryModalVisible: false,
      filmModalMounted: false,
      filmModalVisible: false,
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 375,
      windowHeight: typeof window !== "undefined" ? window.innerHeight : 635,
      safariWidth: typeof window !== "undefined" ? window.innerWidth : 635,
      safariHeight: typeof window !== "undefined" ? window.innerHeight : 635,
      galleryOpenIndex: 0,
      shouldOpenVideo: false
    };

    componentDidMount() {
      const { location } = this.props;

      // Reset navbar state on mount
      if (location.state && location.state.fromNavbar) {
        navigate(location.pathname, { state: { fromNavbar: false } });
      }

      const parsed = qs.parse(location.search);

      if (parsed?.showVideo) {
        this.setState({ shouldOpenVideo: true });
      }

      this.setState({
        hasMounted: true,
        // Calculate once client mounts
        windowHeight:
          document.documentElement?.clientHeight || window.innerHeight,
        windowWidth: window.innerWidth,
        safariWidth: document.documentElement?.clientWidth || window.innerWidth,
        safariHeight: window.innerHeight
      });

      setTimeout(this.handleWindowResize);

      window.addEventListener("keydown", this.handleFirstTab);
      window.addEventListener("resize", this.handleWindowResize);
    }

    componentDidUpdate(prevProps, prevState) {
      const { location } = this.props;
      const { shouldOpenVideo, userIsTabbing } = this.state;

      // If location changes
      if (location.pathname !== prevProps.location.pathname) {
        // Reset tabbing focus
        if (userIsTabbing) {
          this.setState({ userIsTabbing: false });
          window.removeEventListener("keydown", this.handleFirstTab);
          window.addEventListener("keydown", this.handleFirstSelect);
          window.addEventListener("mousedown", this.handleMouseDownOnce);
        }
      }

      // Open film modal from query param
      if (shouldOpenVideo) {
        this.setState({ shouldOpenVideo: false });
        setTimeout(this.handleOpenFilm, 100);
      }
    }

    componentWillUnmount() {
      window.removeEventListener("mousedown", this.handleMouseDownOnce);
      window.removeEventListener("keydown", this.handleFirstSelect);
      window.removeEventListener("keydown", this.handleFirstTab);
      window.removeEventListener("resize", this.handleWindowResize);
    }

    handleWindowResize = debounce(() => {
      this.setState({
        windowWidth: window.innerWidth,
        windowHeight:
          document.documentElement?.clientHeight || window.innerHeight,
        safariWidth: document.documentElement?.clientWidth || window.innerWidth,
        safariHeight: window.innerHeight
      });
    }, 33);

    handleFirstTab = event => {
      // User is tabbing to navigate
      if (event.keyCode === 9) {
        this.setState({ userIsTabbing: true });
        window.removeEventListener("keydown", this.handleFirstTab);
        window.addEventListener("keydown", this.handleFirstSelect);
        window.addEventListener("mousedown", this.handleMouseDownOnce);
      }
    };

    handleFirstSelect = event => {
      // User selected an item
      if (event.key === "Enter") {
        this.handleMouseDownOnce();
      }
    };

    handleMouseDownOnce = () => {
      this.setState({ userIsTabbing: false });
      window.removeEventListener("mousedown", this.handleMouseDownOnce);
      window.removeEventListener("keydown", this.handleFirstSelect);
      window.addEventListener("keydown", this.handleFirstTab);
    };

    handleOpenGallery = openToIndex => {
      // Disable scroll
      lockBodyScrolling();
      // Open modal
      this.setState({
        galleryModalMounted: true,
        galleryModalVisible: true,
        galleryOpenIndex: openToIndex
      });
    };

    handleCloseGallery = () => {
      // Enable scroll
      restoreBodyScrolling();
      // Start close animation
      this.setState({
        galleryModalVisible: false
      });
    };

    handleCloseGalleryComplete = () =>
      this.setState({ galleryModalMounted: false });

    handleOpenFilm = () => {
      // Disable scroll
      lockBodyScrolling();
      // Open modal
      this.setState({ filmModalMounted: true, filmModalVisible: true });
    };

    handleCloseFilm = () => {
      // Enable scroll
      restoreBodyScrolling();
      // Start close animation
      this.setState({
        filmModalVisible: false
      });
    };

    handleCloseFilmComplete = () => this.setState({ filmModalMounted: false });

    render() {
      const {
        hasMounted,
        userIsTabbing,
        galleryModalMounted,
        galleryModalVisible,
        filmModalMounted,
        filmModalVisible,
        windowWidth,
        windowHeight,
        safariWidth,
        safariHeight,
        galleryOpenIndex
      } = this.state;

      return (
        <ScreenClassProvider>
          <ScreenClassRender
            fallbackScreenClass="xs"
            render={screenClass => {
              const isDesktop = _isDesktop(hasMounted, screenClass);

              return (
                <>
                  {isMobileOnly && hasMounted ? (
                    <LandscapeBlock
                      className={cx(globalStyles.image, styles.landscapeBlock)}
                    />
                  ) : null}

                  <WrappedComponent
                    {...this.props}
                    screenClass={screenClass}
                    isDesktop={isDesktop}
                    handleOpenGallery={this.handleOpenGallery}
                    handleOpenFilm={this.handleOpenFilm}
                    userIsTabbing={userIsTabbing}
                    windowWidth={windowWidth}
                    windowHeight={windowHeight}
                    safariWidth={safariWidth}
                    safariHeight={safariHeight}
                  />

                  {galleryModalMounted ? (
                    <Dimmer
                      visible={galleryModalVisible}
                      onClose={this.handleCloseGalleryComplete}
                      customAnimation={state => globalStyles[state]}
                      className={cx(
                        styles.galleryDimmer,
                        globalStyles.opacityAnimationFast
                      )}
                    >
                      <GalleryModal
                        visible={galleryModalVisible}
                        galleryOpenIndex={galleryOpenIndex}
                        userIsTabbing={userIsTabbing}
                        screenClass={screenClass}
                        isDesktop={isDesktop}
                        onClose={this.handleCloseGallery}
                      />
                    </Dimmer>
                  ) : null}
                  {filmModalMounted ? (
                    <Dimmer
                      visible={filmModalVisible}
                      onClose={this.handleCloseFilmComplete}
                      customAnimation={state => globalStyles[state]}
                      className={cx(
                        styles.galleryDimmer,
                        globalStyles.opacityAnimationFast
                      )}
                    >
                      <FilmModal
                        userIsTabbing={userIsTabbing}
                        isVisible={filmModalVisible}
                        screenClass={screenClass}
                        isDesktop={isDesktop}
                        onClose={this.handleCloseFilm}
                      />
                    </Dimmer>
                  ) : null}
                </>
              );
            }}
          />
        </ScreenClassProvider>
      );
    }
  };
};

export default withModals;
